// Noise filters for Sentry ingestion (JURYBOX-WEB-APP-18, -19).
//
// Browser extensions inject their scripts into the page and their failures
// surface through the very same global handlers the app installs, so an
// extension's bugs arrive in Jurybox's Sentry project looking like ours.
// Nothing in this codebase can fix a third party's extension, and the volume
// crowds real signal out of triage: a single PayPal Honey install produced
// three new issue groups in one page load.
//
// Two distinct shapes need two distinct filters, because they arrive with
// different amounts of evidence:
//
//   1. The extension's script IS the top stack frame — a file inside the
//      extension bundle. `denyUrls` matches on that frame, so
//      EXTENSION_URL_PATTERNS handles this case.
//   2. There is no stack frame at all. An extension dispatches a synthetic
//      `unhandledrejection` at `window`, and the SDK captures the DOM Event
//      object itself as the "rejection reason". `denyUrls` cannot see these —
//      there is no URL to match — so isDomEventRejection catches them by the
//      shape of the captured value instead.

/**
 * URL fragments that identify a browser-extension script rather than app code.
 *
 * Covers the `*-extension://` schemes, plus the two on-disk layouts Safari
 * uses for extensions shipped inside a native app bundle
 * (`.safariextension/` resource directories and `Extension.appex/` App
 * Extension bundles) — the latter is what JURYBOX-WEB-APP-18 reports.
 *
 * On the scheme list: Chromium browsers (Chrome, Edge, Brave, Opera) all use
 * `chrome-extension://`, so that one entry covers Edge too.
 * `ms-browser-extension://` is legacy EdgeHTML only — kept because it costs
 * nothing, not because modern Edge needs it.
 */
export const EXTENSION_URL_PATTERNS = [
  /^(?:chrome|chrome-untrusted|moz|safari|safari-web|ms-browser)-extension:\/\//i,
  /\.safariextension\//i,
  /\/Extension\.appex\//i,
];

/**
 * True when the value a promise rejected with is a DOM Event.
 *
 * No first-party code here rejects with one: every `reject()` call site in
 * `src` (`fetchWithTimeout`, `asyncActions`, `WebSocketClient`, `Root.jsx`)
 * passes an Error or a caught exception, and Dexie/IndexedDB and Web Crypto
 * failures surface as `DOMException`, which is not an `Event`. What we see
 * instead is an extension re-dispatching a synthetic `unhandledrejection` at
 * the window, which the SDK's global handler captures verbatim.
 *
 * Scope worth being honest about: that enumeration covers `src`, not our
 * dependencies. A third-party library that rejected with a `ProgressEvent`
 * from a storage or IDB handler would be dropped here too. That is accepted
 * rather than overlooked — such a rejection carries no stack, no message and
 * no app frames, which is the same reason this filter exists at all — but it
 * is a wider net than "app code never does this" alone would justify.
 *
 * @param {{originalException?: unknown}} [hint] - Sentry's beforeSend hint
 * @returns {boolean} true when the event should be dropped
 */
export function isDomEventRejection(hint) {
  if (typeof Event === 'undefined') {
    return false;
  }
  return hint?.originalException instanceof Event;
}

/**
 * Sentry `beforeSend` hook: drop known-unactionable third-party noise.
 *
 * Kept deliberately narrow — it only discards events this app provably cannot
 * produce. Anything ambiguous is left to reach Sentry, where it can be triaged
 * against real evidence rather than silently dropped at the client.
 *
 * @param {object} event - the Sentry event
 * @param {object} [hint] - the accompanying hint, carrying originalException
 * @returns {object|null} the event to send, or null to drop it
 */
export function beforeSend(event, hint) {
  if (isDomEventRejection(hint)) {
    return null;
  }
  return event;
}
